import { useEffect, useMemo, useRef } from "react";

import { experiments, getExperimentWithExposures } from "@/utils/experiment";
import { useFeatureExposed } from "@/utils/feature-exposed";

import { useCloudStatus } from "./use-cloud-status";
import { useCloudStatusRedirection } from "./use-cloud-status-redirection";
import { useCreateMatureExampleProjectV2 } from "./use-create-mature-example-project-v2";
import { useCreateSampleProject } from "./use-create-sample-project";

/**
 * Hook to handle creating a mature example project based on experiment enrollments.
 */
export const useMatureExampleProjectWithExperiments = (
    isEligibleForMatureExampleProjectCreation: boolean
) => {
    const { addBlockingRedirectPromise } = useCloudStatusRedirection();
    const createSampleProjectRequestInitialized = useRef(false);
    const { createMatureExampleProjectV2 } = useCreateMatureExampleProjectV2();
    const { createSampleProject } = useCreateSampleProject();
    const { createCloudStatus } = useCloudStatus();

    const isMatureExampleProjectV2Variation = useMemo(() => {
        if (!isEligibleForMatureExampleProjectCreation) {
            return false;
        }

        const sampleProjectExperiment = getExperimentWithExposures(
            experiments.jfindMatureExampleProjectV2Experiment
        );

        return (
            sampleProjectExperiment.type === "enrolled" &&
            sampleProjectExperiment.value
        );
    }, [isEligibleForMatureExampleProjectCreation]);

    const isMatureSampleProjectV1Variation = useMemo(() => {
        if (!isEligibleForMatureExampleProjectCreation) {
            return false;
        }

        const sampleProjectExperiment = getExperimentWithExposures(
            experiments.jfindMatureExampleProjectExperiment
        );

        return (
            sampleProjectExperiment.type === "enrolled" &&
            sampleProjectExperiment.value
        );
    }, [isEligibleForMatureExampleProjectCreation]);

    useFeatureExposed("jfind-mature-example-project-v2", "Test", {
        conditions: [
            isEligibleForMatureExampleProjectCreation &&
                createCloudStatus === "success" &&
                isMatureExampleProjectV2Variation
        ]
    });

    useFeatureExposed("jfind-mature-example-project", "Test", {
        conditions: [
            isEligibleForMatureExampleProjectCreation &&
                createCloudStatus === "success" &&
                isMatureSampleProjectV1Variation
        ]
    });

    useEffect(() => {
        if (
            isMatureExampleProjectV2Variation &&
            createCloudStatus === "success" &&
            createSampleProjectRequestInitialized.current === false
        ) {
            createSampleProjectRequestInitialized.current = true;
            const matureExampleProjectV2Promise =
                createMatureExampleProjectV2();
            addBlockingRedirectPromise(matureExampleProjectV2Promise);
        } else if (
            isMatureSampleProjectV1Variation &&
            createCloudStatus === "success" &&
            createSampleProjectRequestInitialized.current === false
        ) {
            createSampleProjectRequestInitialized.current = true;
            const matureExampleProjectV1Promise = createSampleProject();
            addBlockingRedirectPromise(matureExampleProjectV1Promise);
        }
    }, [
        isMatureExampleProjectV2Variation,
        isMatureSampleProjectV1Variation,
        createCloudStatus,
        addBlockingRedirectPromise,
        createSampleProject
    ]);
};
